import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useLanguage } from '../../contexts/language/LanguageContext';
import { useNavbar } from '../../contexts/navigation/NavbarContext';
import { InstagramIcon } from '../icons';
import { CONTACT_INFO } from '../../helpers/constants.helper';
import MobileMenu from './MobileMenu';
import './Navigation.scss';

const Navigation = () => {
  const { language, setLanguage, t } = useLanguage();
  const { hideNav, activeHomeSection, goToVideoSection, goToPortfolioSection } = useNavbar();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLanguageChange = (lang: 'en' | 'uk'): void => {
    if (language !== lang) {
      setLanguage(lang);
    }
  };

  // Визначаємо, чи ми на головній сторінці (/ або /portfolio)
  const isHomePage = location.pathname === '/' || location.pathname === '/portfolio';

  // Handle logo click - go to video section if on home page
  const handleLogoClick = (e: React.MouseEvent) => {
    if (isHomePage && goToVideoSection) {
      e.preventDefault();
      goToVideoSection();
    }
  };

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle portfolio click - use callback if already on home page
  const handlePortfolioClick = (e: React.MouseEvent) => {
    if (isHomePage && goToPortfolioSection) {
      e.preventDefault();
      goToPortfolioSection();
    }
  };

  // Determine if Portfolio link should be active
  const isPortfolioActive =
    location.pathname === '/portfolio' ||
    (location.pathname === '/' && activeHomeSection === 1);

  // Determine NavBar theme based on pathname for smooth transitions
  // Use data attributes instead of classes to avoid CSS conflicts
  const getTheme = (): 'light' | 'dark' | 'transparent' => {
    if (location.pathname === '/portfolio') return 'dark';
    if (location.pathname === '/' && (activeHomeSection === null || activeHomeSection === 0)) return 'transparent';
    if (location.pathname === '/' && activeHomeSection !== null && activeHomeSection !== 0) return 'dark';
    return 'light'; // About, Services, Contact
  };

  const theme = getTheme();

  return (
    <nav
      className={`navigation ${isHomePage && hideNav ? 'hidden' : ''}`}
      data-theme={theme}
      data-scrolled={!isHomePage && isScrolled}
    >
      <div className="navigation__container">
        {/* Left: Logo */}
        <div className="navigation__left">
          {(location.pathname !== '/' || activeHomeSection !== 0) && (
            <Link to="/" className="navigation__logo" onClick={handleLogoClick}>
              ALINA POLONEYEVA
            </Link>
          )}
        </div>

        {/* Center: Menu + Language Switcher */}
        <div className="navigation__center">
          <div className="navigation__menu">
            <Link
              to="/portfolio"
              className={isPortfolioActive ? 'active' : ''}
              onClick={handlePortfolioClick}
            >
              {t.nav.portfolio}
            </Link>
            <Link to="/services" className={location.pathname === '/services' ? 'active' : ''}>
              {t.nav.terms}
            </Link>
            <Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>
              {t.nav.about}
            </Link>
            <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>
              {t.nav.contact}
            </Link>
          </div>
          <div className="navigation__lang-switcher">
            <button
              onClick={() => handleLanguageChange('en')}
              className={`navigation__lang ${language === 'en' ? 'active' : ''}`}
            >
              EN
            </button>
            <span className="navigation__lang-separator">|</span>
            <button
              onClick={() => handleLanguageChange('uk')}
              className={`navigation__lang ${language === 'uk' ? 'active' : ''}`}
            >
              UA
            </button>
          </div>
        </div>

        {/* Right: Contact Icons + Hamburger */}
        <div className="navigation__right">
          <div className="navigation__contact-icons">
            <a
              href={`mailto:${CONTACT_INFO.email}`}
              className="navigation__icon-link"
              aria-label="Email"
            >
              <Mail size={18} />
            </a>
            <a
              href={CONTACT_INFO.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="navigation__icon-link"
              aria-label="Instagram"
            >
              <InstagramIcon size={18} />
            </a>
          </div>
          <button
            className="navigation__hamburger"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </nav>
  );
};

export default Navigation;
